import React, {useEffect, useRef} from 'react';
import {Container, Row, Col, Accordion} from 'react-bootstrap';
import {useClarity} from '../hooks/useClarity';

const faqs = [
    {
        question: "How is this different from just using ChatGPT or another LLM?",
        answer: (
            <>
                <p>
                    Large language models are built for fluency, not for evidence.
                    They can fabricate citations, give different answers to the same
                    question on different days, and offer no audit trail back to
                    source. For regulated work — financial advice, clinical decisions,
                    compliance checks — that's a deployment blocker, not a tuning problem.
                </p>
                <p className="mb-0">
                    Nebulyx Cognition takes a different approach. Structure is extracted
                    directly from your source documents. Every output cites the document,
                    page and paragraph it came from. The same question always returns
                    the same answer.
                </p>
            </>
        )
    },
    {
        question: "Why not just build a proper enterprise model first?",
        answer: (
            <>
                <p>
                    Classical ontology and knowledge-engineering approaches were
                    designed for stable libraries — not for businesses that change
                    every quarter. They typically work for the first view of a
                    domain and shatter when a second view is added. Each new view
                    triggers a remodel. Two-year programmes with no shipped use
                    case are common.
                </p>
                <p className="mb-0">
                    Our approach captures each domain as its own scoped knowledge
                    base, in days. Agents reason inside a domain when the decision
                    is local, and across domains through entity resolution at the
                    seams — without trying to model the whole enterprise upfront.
                </p>
            </>
        )
    },
    {
        question: "Where does our data go? Is this UK-sovereign?",
        answer: (
            <>
                <p>
                    Yes. Nebulyx Cognition models are typically under 1 GB and can run entirely on
                    your infrastructure or a UK-based environment of your choice.
                    Nothing leaves your control. No data is sent to a third-party
                    LLM provider.
                </p>
                <p className="mb-0">
                    For regulated industries this isn't optional and we've built
                    the platform to meet that constraint from day one. Deployment
                    options include on-premise, private cloud, or a dedicated UK
                    region.
                </p>
            </>
        )
    },
    {
        question: "How long from first call to a working pilot?",
        answer: (
            <>
                <p>
                    Three to four weeks. The first hour is a discovery call where
                    we agree the decision worth automating first. Weeks one to
                    three are the build — your subject-matter experts shape the
                    knowledge base as it takes shape. Week four is demo day:
                    a live system your stakeholders can use against real cases.
                </p>
                <p className="mb-0">
                    No multi-year programme. No slide-deck-only deliverables.
                    A working tool you can evaluate.
                </p>
            </>
        )
    },
    {
        question: "What happens when our content gets amended?",
        answer: (
            <>
                <p>
                    Just the relevant knowledge base updates. Amendments, overrides
                    and reinterpretations are tracked natively as typed relationships
                    in the graph — not as manual remodelling exercises. The rest of
                    the business carries on uninterrupted.
                </p>
                <p className="mb-0">
                    When an auditor asks which version of which rule drove a
                    decision in March, the answer is one query away — cited
                    back to document, page, paragraph.
                </p>
            </>
        )
    },
    {
        question: "How does it handle our subject-matter experts and their judgement?",
        answer: (
            <>
                <p>
                    Experts shape what we call <em>the last 20%</em>. They highlight,
                    classify, correct, and override through the interface — not by
                    hiring an ontologist. Every override is captured as first-class
                    data and flows back into the system, so the next decision starts
                    smarter than the last one.
                </p>
                <p className="mb-0">
                    Critically, the agent never replaces expert judgement. It
                    surfaces the right context for the decision and records the
                    reasoning — the expert stays in control.
                </p>
            </>
        )
    },
    {
        question: "Can we start small and expand?",
        answer: (
            <>
                <p>
                    Yes. Each pilot scopes to one decision, one domain. New domains
                    plug in alongside the first — each one ships faster than the
                    last as the platform learns the shape of your regulatory and
                    operational landscape.
                </p>
                <p className="mb-0">
                    First domain expensive. Second cheaper. Third mostly automatic.
                </p>
            </>
        )
    }
];

const FAQSection = () => {
    const {trackEvent} = useClarity();
    const sectionRef = useRef(null);
    const hasTrackedView = useRef(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && !hasTrackedView.current) {
                        hasTrackedView.current = true;
                        trackEvent('section_viewed', {
                            section: 'faq',
                            position: 7
                        });
                    }
                });
            },
            {threshold: 0.3}
        );
        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }
        return () => observer.disconnect();
    }, [trackEvent]);

    const handleAccordionSelect = (eventKey) => {
        if (eventKey !== null) {
            const faqIndex = parseInt(eventKey);
            const faq = faqs[faqIndex];
            trackEvent('faq_opened', {
                question: faq.question,
                faq_index: faqIndex,
                location: 'homepage'
            });
        }
    };

    return (
        <section className="faq-section" ref={sectionRef}>
            <Container>
                <div className="text-center mb-4">
                    <div className="section-label">Common Questions</div>
                    <h2>What buyers in regulated industries actually ask.</h2>
                    <p className="section-intro">
                        The questions a CDO, Head of Compliance, or Head of AI
                        brings to a first conversation. Honest answers, not
                        marketing copy.
                    </p>
                </div>

                <Row className="justify-content-center mt-4">
                    <Col lg={10}>
                        <Accordion
                            className="faq-accordion"
                            onSelect={handleAccordionSelect}>
                            {faqs.map((faq, index) => (
                                <Accordion.Item eventKey={index.toString()} key={index}>
                                    <Accordion.Header>
                                        {faq.question}
                                    </Accordion.Header>
                                    <Accordion.Body>
                                        {faq.answer}
                                    </Accordion.Body>
                                </Accordion.Item>
                            ))}
                        </Accordion>
                    </Col>
                </Row>
            </Container>
        </section>
    );
};

export default FAQSection;